import TR from 'utils/TR';
import T from 'utils/T';
import ex from 'utils/ex';
import React from 'react';
import useRect from 'hooks/common/useRect';
import Recoil from 'recoil';
import * as AppStore from 'stores/AppStore';

import { Chart } from 'chart.js';
import useSetChartData from 'hooks/app/useSetChartData';

const DEFAULT_VALUE = NaN;
const columns = Array.from({ length: 25 }).map((name, index) => `${index.toString().padStart(2, '0')}00`);
const config = (agpHash, hash, barColor, axisName, unit, max, min, stepSize, onComplete, duration) => ({
  data: {
    labels: columns,
    datasets: [{
      type: 'line',
      data: columns.map((name, index) => {
        return agpHash[name]?.bld_sugr_5per || DEFAULT_VALUE;
      }),
      pointRadius: 0,
      borderWidth: 0,
      cubicInterpolationMode: 'monotone',
      tension: 0.4,
      yAxisID: 'yy',
    }, {
      type: 'line',
      data: columns.map((name, index) => {
        return agpHash[name]?.bld_sugr_95per || DEFAULT_VALUE;
      }),
      pointRadius: 0,
      borderWidth: 0,
      fill: '-1',
      cubicInterpolationMode: 'monotone',
      backgroundColor: '#347AFF20',
      tension: 0.4,
      yAxisID: 'yy',
    }, {
      type: 'line',
      data: columns.map((name, index) => {
        return agpHash[name]?.bld_sugr_25per || DEFAULT_VALUE;
      }),
      pointRadius: 0,
      borderWidth: 0,
      cubicInterpolationMode: 'monotone',
      tension: 0.4,
      yAxisID: 'yy',
    }, {
      type: 'line',
      data: columns.map((name, index) => {
        return agpHash[name]?.bld_sugr_75per || DEFAULT_VALUE;
      }),
      pointRadius: 0,
      borderWidth: 0,
      fill: '-1',
      cubicInterpolationMode: 'monotone',
      backgroundColor: '#347AFF20',
      tension: 0.4,
      yAxisID: 'yy',
    }, {
      type: 'line',
      data: columns.map((name, index) => {
        return agpHash[name]?.bld_sugr_50per || DEFAULT_VALUE;
      }),
      pointRadius: 0,
      borderWidth: 3,
      cubicInterpolationMode: 'monotone',
      borderColor: '#2251A9',
      tension: 0.4,
      yAxisID: 'yy',
    }, {
      type: 'bar',
      data: columns.map((time) => hash[time]?.min || [0, 0]),
      bordorColor: barColor,
      borderWidth: 0.5,
      barThickness: 1,
      backgroundColor: barColor,
      barPercentage: 1,
      stack: '0',
    }, {
      type: 'bar',
      data: columns.map((time) => hash[time]?.per25 || 0),
      bordorColor: barColor,
      borderWidth: 0.5,
      barThickness: 10,
      backgroundColor: barColor,
      barPercentage: 1,
      stack: '0',
    }, {
      type: 'bar',
      data: columns.map((time) => hash[time]?.per50 || 0),
      barThickness: 10,
      backgroundColor: '#ffffff',
      barPercentage: 1,
      stack: '0',
    }, {
      type: 'bar',
      data: columns.map((time) => hash[time]?.per75 || 0),
      bordorColor: barColor,
      borderWidth: 0.5,
      barThickness: 10,
      backgroundColor: barColor,
      barPercentage: 1,
      stack: '0',
    }, {
      type: 'bar',
      data: columns.map((time) => hash[time]?.max || 0),
      bordorColor: barColor,
      borderWidth: 0.5,
      barThickness: 1,
      backgroundColor: barColor,
      barPercentage: 1,
      stack: '0',
    }],
  },
  options: {
    devicePixelRatio: 4,
    animation: {
      duration,
      onComplete,
    },
    layout: {
      padding: {
        top: 50,
        right: 0,
        // left: 30,
        left: 180,
      },
    },
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        border: {
          width: 1,
          color: '#eeeeee',
        },
        grid: {
          display: false,
          offsetGridLines: false,
          drawTicks: false,
          drawOnChartArea: true,
        },
        stacked: true,
        ticks: {
          fontSize: 14,
          callback: (value) => {
            if (value % 6 === 0) {
              return value + 'H';
            } else {
              return '';
            }
          },
        },
      },
      y: {
        min,
        max,
        border: {
          dash: [4, 4],
        },
        grid: {
          display: true,
          drawTicks: false,
          drawOnChartArea: true,
        },
        stacked: true,
        ticks: {
          fontSize: 11,
          color: '#AAAAAA',
          stepSize,
        },
      },
      yy: {
        position: 'right',
        type: 'logarithmic',
        display: true,
        afterTickToLabelConversion: (scaleInstance) => {
          const ticks = scaleInstance.ticks;
          const newTicks = ticks?.filter((tick) => {
            switch (tick.value) {
              case 0: return true;
              case 54: return true;
              case 70: return true;
              case 180: return true;
              case 250: return true;
              case 500: return true;
              default: return false;
            }
          });
          scaleInstance.ticks = newTicks;
        },
        title: {
          display: true,
        },
        min: 40,
        max: 500,
        ticks: {
          callback: (value) => {
            switch (value) {
              case 0:
              case 54:
              case 70:
              case 180:
              case 250:
              case 500:
                return value;
              default:
                return '';
            }
          },
          // stepSize: 1,
          // display: false,
        },
        afterBuildTicks: (chart) => {
          chart.ticks = [];
          chart.ticks.push({ value: 54 });
          chart.ticks.push({ value: 70 });
          chart.ticks.push({ value: 180 });
          chart.ticks.push({ value: 250 });
          chart.ticks.push({ value: 500 });
        },
        grid: {
          drawTicks: false,
          color: function (context) {
            switch (context.tick.value) {
              case 180:
              case 70:
                return '#0FCF65';
              default:
                return '#cccccc';
            }
          }
        },
        border: {
          dash: (context) => {
            switch (context.tick.value) {
              case 180:
              case 70:
                return [5, 5];
              default: break;
            }
          }
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: true,
        callbacks: {
          title: function (context) {
            const label = (context[0].label || '').split('');
            label.splice(2, 0, ':');
            return label.join('');
          }
        },
      },
    },
  },
  plugins: [{
    afterDatasetDraw: (chart, args, options) => {
      const ctx = chart.ctx;
      const yAxis = chart.scales.y;
      const yyAxis = chart.scales.yy;
      const xAxis = chart.scales.x;

      const meta = args.meta;
      const dataset = meta.controller.getDataset();
      if (!dataset) return;

      if (!yAxis) return;
      if (!xAxis) return;

      const left = xAxis.left;
      const right = xAxis.right;

      ctx.save();

      ctx.font = '12px Pretendard';
      ctx.fillStyle = '#aaaaaa';
      const y = yAxis.getPixelForValue(max);

      ctx.textAlign = 'right';
      ctx.textBaseline = 'bottom';
      ctx.fillText(axisName, left, y - 30);
      ctx.fillText(T('({0})', unit), left, y - 13);


      ctx.textAlign = 'left';
      ctx.textBaseline = 'bottom';
      ctx.fillText(T('평균혈당'), right, y - 30);

      ctx.restore();
    }
  }]
});

export default function ExercisePatternChart(props) {
  const {
    viewMode,
    stepData,
    caloryData,
    duration,
    ...rest
  } = props;


  const setChartData1 = useSetChartData('StepAnalysisDayChart');
  const setChartData2 = useSetChartData('CaloryAnalysisDayChart');
  const setChartData = React.useMemo(() => {
    if (viewMode === 'ReportExerciseSteps') {
      return setChartData1;
    } else {
      return setChartData2;
    }
  }, [setChartData1, setChartData2, viewMode]);

  const chartDataTimeoutRef = React.useRef();
  const onComplete = React.useCallback((animation) => {
    if (duration) return;
    if (!animation.chart.canvas.width) return;
    if (!animation.chart.canvas.height) return;
    clearTimeout(chartDataTimeoutRef.current);
    const url = animation.chart.canvas.toDataURL('image/png');
    const width = animation.chart.canvas.width;
    const height = animation.chart.canvas.height;
    chartDataTimeoutRef.current = setTimeout(() => {
      setChartData && setChartData(() => ({ url, width, height }));
      URL.revokeObjectURL(url);
    }, 10000);
  }, [duration, setChartData]);

  const agp_base = Recoil.useRecoilValue(AppStore.assets.agp_base);
  const agpHash = React.useMemo(() => agp_base.reduce((a, c) => ({
    ...a,
    [c.time]: c,
  }), {}), [agp_base]);

  const stepMaxRef = React.useRef(1000);
  const stepHash = React.useMemo(() => {
    const {
      active_pattern_time_graph_list = [],
      active_pattern_time_list = [],
    } = stepData || {};
    stepMaxRef.current = 1000;
    const hash = active_pattern_time_graph_list.reduce((a, c) => {
      if (stepMaxRef.current < c.max_step_cnt) stepMaxRef.current = c.max_step_cnt;
      return {
        ...a,
        [(c.time + '00').padStart(4, '0')]: {
          min: [c.min_step_cnt, c.step_cnt_25per],
          max: c.max_step_cnt - c.step_cnt_75per,
          per25: c.step_cnt_50per - c.step_cnt_25per - 1,
          per50: 1,
          per75: c.step_cnt_75per - c.step_cnt_50per,
        },
      };
    }, {});
    return active_pattern_time_list.reduce((a, c) => ({
      ...a,
      [c.time]: {
        ...a[c.time],
        line: c.avg_heart_rate,
      }
    }), hash);
  }, [stepData]);

  const caloryMaxRef = React.useRef(200);
  const caloryHash = React.useMemo(() => {
    const {
      active_pattern_time_graph_list = [],
      active_pattern_time_list = [],
    } = caloryData || {};
    caloryMaxRef.current = 200;
    const hash = active_pattern_time_graph_list.reduce((a, c) => {
      if (caloryMaxRef.current < c.max_cal_amt) caloryMaxRef.current = c.max_cal_amt;
      return {
        ...a,
        [(c.time + '00').padStart(4, '0')]: {
          min: [c.min_cal_amt, c.cal_amt_25per],
          max: c.max_cal_amt - c.cal_amt_75per,
          per25: c.cal_amt_50per - c.cal_amt_25per - 1,
          per50: 1,
          per75: c.cal_amt_75per - c.cal_amt_50per,
        },
      };
    }, {});
    return active_pattern_time_list.reduce((a, c) => ({
      ...a,
      [c.time]: {
        ...a[c.time],
        line: c.avg_heart_rate,
      }
    }), hash);
  }, [caloryData]);

  const ref = React.useRef();
  const rect = useRect(ref);
  const canvasRef = React.useRef();
  const chartRef = React.useRef();
  React.useEffect(() => {
    if (viewMode === 'ReportExerciseSteps') {
      const max = Math.ceil(stepMaxRef.current / 1000) * 1000;
      const data = config(agpHash, stepHash, '#4d79fb', '걸음수', '보', max, 0, 1000, onComplete, duration)
      const chart = new Chart(canvasRef.current, data);
      chartRef.current = chart;
    } else if (viewMode === 'ReportExerciseCalories') {
      const max = Math.ceil(caloryMaxRef.current / 100) * 100;
      const data = config(agpHash, caloryHash, '#4d79fb', '소모칼로리', 'kcal', max, 0, 100, onComplete, duration)
      const chart = new Chart(canvasRef.current, data);
      chartRef.current = chart;
    }
    return () => {
      chartRef.current.destroy();
      delete chartRef.current.config;
      chartRef.current = null;
    };
  }, [viewMode, stepHash, caloryHash, agpHash, onComplete, duration]);

  React.useLayoutEffect(() => {
    if (!chartRef.current) return;
    const frameId = requestAnimationFrame(() => {
      chartRef.current.config.options.layout.padding.left = Math.floor(rect.width / 6.5);
      chartRef.current.update();
    });
    return () => {
      cancelAnimationFrame(frameId);
    };
  }, [rect]);

  return (
    <div {...ex('ExercisePatternChart', rest)}>
      <div {...ex('Chart', { ref })}>
        <canvas
          {...{
            ref: canvasRef,
            width: rect.width,
            height: rect.height,
          }}
        />
      </div>
    </div>
  );
}
